import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const CampaignPreview = ({ creative, contentText, contentCta, brandColor, brandName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 transition-all duration-200 hover:shadow-xl">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700">
          👁️ Campaign Preview
        </h3>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          {isCollapsed ? (
            <ChevronDown className="text-slate-400" size={18} />
          ) : (
            <ChevronUp className="text-slate-400" size={18} />
          )}
        </button>
      </div>
      
      {!isCollapsed && (
        <>
          {/* WhatsApp Light Theme Preview */}
          <div className="rounded-2xl overflow-hidden max-w-sm mx-auto shadow-2xl border border-slate-300">
            {/* WhatsApp Header - Green */}
            <div className="px-4 py-3 flex items-center justify-between bg-[#075E54]">
              <div className="flex items-center gap-3">
                {/* Brand Logo Circle */}
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <span className="text-[#075E54] font-bold text-lg">
                    {brandName ? brandName.charAt(0).toUpperCase() : 'W'}
                  </span>
                </div>
                <div>
                  <p className="text-base font-medium text-white">{brandName || 'Wellversed'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button className="text-white hover:bg-white/10 p-1 rounded">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </button>
                <button className="text-white hover:bg-white/10 p-1 rounded">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Chat Background - Light WhatsApp Pattern */}
            <div 
              className="min-h-[550px] p-4 flex flex-col justify-end"
              style={{
                backgroundColor: '#efeae2',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            >
              {/* Only show campaign message if there's content */}
              {(creative || contentText || contentCta) ? (
                <>
                  {/* Date Divider */}
                  <div className="flex items-center justify-center mb-6">
                    <div className="px-4 py-1.5 bg-white/90 rounded-lg shadow-sm">
                      <p className="text-xs text-slate-600 font-medium">TODAY</p>
                    </div>
                  </div>

                  {/* WhatsApp Message Bubble */}
                  <div className="flex justify-start mb-2">
                    <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[85%]">
                      {/* Creative Image */}
                      {creative && (
                        <div className="relative">
                          <img
                            src={creative}
                            alt="Campaign creative"
                            className="w-full h-auto"
                            style={{ maxHeight: '300px', objectFit: 'cover' }}
                          />
                        </div>
                      )}
                      
                      {/* Message Text */}
                      {contentText && (
                        <div className="px-4 py-3">
                          <p 
                            className="text-[15px] text-slate-800 whitespace-pre-wrap leading-relaxed font-normal"
                            dangerouslySetInnerHTML={{
                              __html: contentText
                                // Convert *text* to bold
                                .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
                                // Convert _text_ to italic
                                .replace(/_(.*?)_/g, '<em>$1</em>')
                            }}
                          />
                        </div>
                      )}

                      {/* CTA Button */}
                      {contentCta && (
                        <div className="px-4 pb-3">
                          <button 
                            className="w-full py-2.5 rounded-lg text-[#00a5f4] text-sm font-semibold border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-all flex items-center justify-center gap-2"
                            disabled
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {contentCta}
                          </button>
                        </div>
                      )}

                      {/* Timestamp */}
                      <div className="px-4 pb-2 flex items-center justify-end gap-1">
                        <span className="text-xs text-slate-400">
                          {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Empty state message */
                <div className="flex items-center justify-center h-full">
                  <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-8">
                    <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
                    </svg>
                    <p className="text-sm text-slate-500 font-medium">
                      Your campaign preview will<br/>appear here
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp Input Bar */}
            <div className="bg-[#f0f2f5] px-3 py-2.5 flex items-center gap-2">
              <button className="text-slate-500 hover:text-slate-700 p-1.5 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                </svg>
              </button>
              <div className="flex-1 bg-white rounded-full px-4 py-2.5 flex items-center gap-2 shadow-sm">
                <span className="text-sm text-slate-400 flex-1">Type a message</span>
              </div>
              <button className="text-slate-500 hover:text-slate-700 p-1.5 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
              </button>
              <button 
                className="p-3 rounded-full transition-all hover:opacity-90 shadow-md"
                style={{ backgroundColor: '#25D366' }}
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CampaignPreview;
